import CheckIcon from '@heroicons/react/24/outline/CheckIcon';
import ChevronDownIcon from '@heroicons/react/24/outline/ChevronDownIcon';
import ListBulletIcon from '@heroicons/react/24/outline/ListBulletIcon';
import Squares2X2Icon from '@heroicons/react/24/outline/Squares2X2Icon';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { ReactNode, useId } from 'react';

import { Button } from '@/components/core/Button';
import { Checkbox } from '@/components/core/Checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/core/DropdownMenu/DropdownMenu';
import { ToggleGroup } from '@/components/core/ToggleGroup/ToggleGroup';
import { ToggleGroupItem } from '@/components/core/ToggleGroup/ToggleGroupItem';
import { useTranslation } from '@/hooks/useTranslation';
import {
    DAM_LIBRARY_SORTS,
    type DamLibrarySort,
    findDamLibrarySort,
    isDamLibrarySortAvailable,
} from '@/pages/dam/utils/damLibrarySorts';
import type { DamLibraryViewMode } from '@/pages/dam/utils/damLibraryUrlState';
import { cn } from '@/services/utils';

interface DamLibraryToolbarProps {
    totalCount: number;
    sort: DamLibrarySort;
    onSortChange: (sort: DamLibrarySort) => void;
    viewMode: DamLibraryViewMode;
    /** Switches between card grid and list; the list stays disabled while no handler is wired. */
    onViewModeChange?: (viewMode: DamLibraryViewMode) => void;
    /** Selects all entries; the checkbox stays disabled while no handler is wired. */
    onSelectAll?: (isSelected: boolean) => void;
    isAllSelected?: boolean;
    /** Some but not all entries are selected; the checkbox shows a dash. */
    isSomeSelected?: boolean;
    /** Menu "Card view"; a disabled placeholder is shown while none is passed. */
    cardViewMenu?: ReactNode;
}

const menuItemClassName = cn(
    'tw-flex tw-cursor-pointer tw-select-none tw-items-start tw-gap-2 tw-rounded tw-px-3 tw-py-2 tw-text-t-sm tw-text-t-primary tw-outline-none lg:tw-text-t-base',
    'data-[highlighted]:tw-bg-fill-primary-hover data-[disabled]:tw-cursor-not-allowed data-[disabled]:tw-text-t-inactive',
);

/**
 * Toolbar above the card grid (wireframe `I6392:31615;6379:45321`): "Select all N
 * entries" on the left; grid/list switch, sort menu (`6565:67559`) and "Card view"
 * (`6636:47353`) on the right.
 */
function DamLibraryToolbar({
    totalCount,
    sort,
    onSortChange,
    viewMode,
    onViewModeChange,
    onSelectAll,
    isAllSelected = false,
    isSomeSelected = false,
    cardViewMenu,
}: DamLibraryToolbarProps) {
    const { t } = useTranslation();
    const selectAllId = useId();
    const currentSort = findDamLibrarySort(sort);

    return (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-4 tw-gap-y-2">
            <div className="tw-flex tw-items-center tw-gap-2">
                <Checkbox
                    id={selectAllId}
                    isChecked={isAllSelected}
                    isIndeterminate={isSomeSelected}
                    disabled={!onSelectAll}
                    onCheckedChange={() => onSelectAll?.(!isAllSelected && !isSomeSelected)}
                />
                <label htmlFor={selectAllId} className="tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                    {t('DAM.LIBRARY.SELECT_ALL', { count: totalCount.toLocaleString() })}
                </label>
            </div>

            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <ToggleGroup
                    type="single"
                    value={viewMode}
                    onValueChange={(value) => value && onViewModeChange?.(value as DamLibraryViewMode)}
                    className="tw-flex tw-items-center"
                >
                    <ToggleGroupItem value="grid" variant="icon" aria-label={t('DAM.LIBRARY.VIEW_GRID')}>
                        <Squares2X2Icon aria-hidden className="tw-h-5 tw-w-5 tw-icon-primary lg:tw-h-6 lg:tw-w-6" />
                    </ToggleGroupItem>
                    <ToggleGroupItem value="list" variant="icon" aria-label={t('DAM.LIBRARY.VIEW_LIST')} disabled={!onViewModeChange}>
                        <ListBulletIcon aria-hidden className="tw-h-5 tw-w-5 tw-icon-primary lg:tw-h-6 lg:tw-w-6" />
                    </ToggleGroupItem>
                </ToggleGroup>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost">
                            <span>
                                {t('DAM.LIBRARY.SORTING')}{' '}
                                {currentSort ? t(currentSort.labelKey) : ''}
                            </span>
                            <ChevronDownIcon aria-hidden className="tw-h-4 tw-w-4 tw-icon-primary" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" hasBorder hasPadding={false} className="tw-w-56 tw-py-2">
                        <DropdownMenuPrimitive.RadioGroup value={sort} onValueChange={(value) => onSortChange(value as DamLibrarySort)}>
                            {DAM_LIBRARY_SORTS.map((definition) => {
                                const isAvailable = isDamLibrarySortAvailable(definition);
                                return (
                                    <DropdownMenuPrimitive.RadioItem
                                        key={definition.key}
                                        value={definition.key}
                                        disabled={!isAvailable}
                                        className={menuItemClassName}
                                    >
                                        <span className="tw-mt-0.5 tw-inline-flex tw-h-4 tw-w-4 tw-shrink-0">
                                            <DropdownMenuPrimitive.ItemIndicator>
                                                <CheckIcon aria-hidden className="tw-h-4 tw-w-4 tw-icon-primary" />
                                            </DropdownMenuPrimitive.ItemIndicator>
                                        </span>
                                        <span className="tw-flex tw-flex-col">
                                            <span>{t(definition.labelKey)}</span>
                                            {!isAvailable && (
                                                <span className="tw-text-t-xs tw-text-t-secondary lg:tw-text-t-sm">
                                                    {t('DAM.LIBRARY.SORT_NOT_AVAILABLE')}
                                                </span>
                                            )}
                                        </span>
                                    </DropdownMenuPrimitive.RadioItem>
                                );
                            })}
                        </DropdownMenuPrimitive.RadioGroup>
                    </DropdownMenuContent>
                </DropdownMenu>

                {cardViewMenu ?? (
                    <Button variant="ghost" disabled>
                        <span>{t('DAM.LIBRARY.CARD_VIEW')} {t('DAM.LIBRARY.CARD_VIEW_DEFAULT')}</span>
                        <ChevronDownIcon aria-hidden className="tw-h-4 tw-w-4 tw-icon-inactive" />
                    </Button>
                )}
            </div>
        </div>
    );
}

export { DamLibraryToolbar };
export type { DamLibraryToolbarProps, DamLibraryViewMode };
